import React, { useState } from 'react';
import { 
  Box, Typography, Paper, Grid, Divider, Chip, 
  Accordion, AccordionSummary, AccordionDetails, 
  List, ListItem, ListItemText, Button,
  CircularProgress, Badge
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MessageIcon from '@mui/icons-material/Message';
import StarIcon from '@mui/icons-material/Star';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SubscriptionEditor from './SubscriptionEditor';
import UserDeleteDialog from './UserDeleteDialog';
import UserLocationMap from './UserLocationMap';

const UserDetails = ({ user, onUserUpdated, onUserDeleted }) => {
  const [showRawData, setShowRawData] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  if (!user) return null;
  
  // Subscription update handler
  const handleSubscriptionUpdated = (updatedUser) => {
    if (typeof onUserUpdated === 'function') {
      onUserUpdated(updatedUser);
    }
  };
  
  // Delete dialog handlers
  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };
  
  const handleUserDeleted = (userId) => {
    if (typeof onUserDeleted === 'function') {
      onUserDeleted(userId);
    }
  };

  // Parse data from the user object
  const profile = user.profile || {};
  const geo = user.geo || {};
  const search = user.search || {};
  const settings = user.settings || {};
  const rates = user.rates || {};
  const photos = user.photos || [];
  const notifications = user.notifications || {};
  const subscribtion = user.subscribtion || {};
  const lastArray = user.lastArray || [];
  
  // Helper function to format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    // Handle Firestore timestamp objects
    if (timestamp && typeof timestamp === 'object') {
      // Если объект имеет seconds (Firestore Timestamp)
      if (timestamp.seconds !== undefined) {
        const date = new Date(timestamp.seconds * 1000);
        return date.toLocaleString();
      }
      
      // Если объект имеет метод toDate
      if (typeof timestamp.toDate === 'function') {
        return timestamp.toDate().toLocaleString();
      }
      
      // Если это уже обычный Date
      if (timestamp instanceof Date) {
        return timestamp.toLocaleString();
      }

      // Если это структура _seconds (например, пришедшая от Firebase)
      if (timestamp._seconds) {
        const date = new Date(timestamp._seconds * 1000);
        return date.toLocaleString();
      }
    }
    
    // Пытаемся распарсить как строку
    try {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date.toLocaleString();
      }
    } catch (e) {
      console.error("Error parsing date:", e);
    }
    
    // Если ничего не помогло, отдаем как есть
    return String(timestamp);
  };

  // Helper function to render array data with clickable user IDs
  const renderArray = (array) => {
    if (!array || array.length === 0) return <Typography color="text.secondary">Empty array</Typography>;
    
    // Function to render a user ID as a clickable link
    const renderUserLink = (userId) => {
      const isValidUid = typeof userId === 'string' && userId.length >= 20;
      
      if (isValidUid) {
        return (
          <Button 
            variant="text" 
            size="small" 
            href={`/dashboard/user/${userId}`}
            sx={{ 
              textAlign: 'left', 
              p: 0, 
              minWidth: 'auto', 
              textTransform: 'none',
              fontSize: 'inherit',
              fontWeight: 'normal',
              justifyContent: 'flex-start'
            }}
          >
            {userId}
          </Button>
        );
      } else {
        return <span>{userId}</span>;
      }
    };
    
    return (
      <List dense disablePadding>
        {array.map((item, index) => {
          let content;
          
          if (typeof item === 'object') {
            // Если это объект, пытаемся вывести UID кликабельно, если есть
            if (item && item.uid) {
              content = (
                <>
                  {item.uid ? renderUserLink(item.uid) : null}
                  {Object.keys(item).filter(key => key !== 'uid').map(key => (
                    <Typography component="span" key={key} variant="body2" sx={{ ml: 1 }}>
                      {` • ${key}: ${item[key]}`}
                    </Typography>
                  ))}
                </>
              );
            } else {
              content = JSON.stringify(item);
            }
          } else {
            // Если просто строка, возможно это UID
            content = renderUserLink(item);
          }
          
          return (
            <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
              <ListItemText primary={content} />
            </ListItem>
          );
        })}
      </List>
    );
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
      {/* 
        В этом верхнем контейнере мы добавили flexWrap и увеличили gap,
        чтобы кнопки правильно переносились на мобильном экране.
      */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            {profile.name || 'Unnamed User'}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Username: {profile.userName || 'Not set'}
            {user.searchUserName && ` (Search: ${user.searchUserName})`}
          </Typography>
          <Typography variant="body2">
            User ID: {user.id}
          </Typography>
          <Typography variant="body2">
            Created: {formatDate(user.created)}
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip 
              label={user.type || 'human'} 
              color={user.type === 'bot' ? 'secondary' : 'primary'}
              size="small"
            />
            {user.play && <Chip label="Active" color="success" size="small" />}
            {user.isOnline && <Chip label="Online" color="info" size="small" />}
          </Box>
        </Box>

        {/* Блок кнопок. Добавлен flexWrap, чтобы они переносились при узком экране. */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button 
            variant="outlined" 
            size="small"
            onClick={() => setShowRawData(!showRawData)}
          >
            {showRawData ? 'Hide Raw Data' : 'Show Raw Data'}
          </Button>
          <Button 
            variant="outlined" 
            color="primary"
            size="small"
            href={`/dashboard/user/${user.id}/chats`}
            startIcon={<MessageIcon />}
          >
            View Chats
          </Button>
          <Button 
            variant="outlined" 
            color="primary"
            size="small"
            href={`/dashboard/user/${user.id}/ratings`}
            startIcon={<StarIcon />}
          >
            View Ratings
          </Button>
          <Button 
            variant="outlined" 
            color="error"
            size="small"
            onClick={handleOpenDeleteDialog}
            startIcon={<DeleteIcon />}
          >
            Delete User
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {showRawData ? (
        <pre style={{ 
          whiteSpace: 'pre-wrap', 
          wordBreak: 'break-word', 
          backgroundColor: '#f5f5f5',
          padding: '16px',
          borderRadius: '4px',
          maxHeight: '800px',
          overflow: 'auto'
        }}>
          {JSON.stringify(user, null, 2)}
        </pre>
      ) : (
        <Grid container spacing={3}>
          {/* Profile Information */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Profile</Typography>
            <Box sx={{ pl: 2 }}>
              <Typography><strong>Email:</strong> {profile.email || 'Not set'}</Typography>
              <Typography><strong>Gender:</strong> {profile.gender || 'Not set'}</Typography>
              <Typography><strong>Birthday:</strong> {formatDate(profile.birthstamp)}</Typography>
              <Typography><strong>About Me:</strong> {profile.aboutMe || 'No description'}</Typography>
              <Typography><strong>Avatar:</strong> {profile.avatar ? 'Set' : 'Not set'}</Typography>
              <Typography><strong>Search Name:</strong> {user.searchName || 'Not set'}</Typography>
            </Box>
          </Grid>

          {/* Geo Information */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Location</Typography>
            <Box sx={{ pl: 2 }}>
              <Typography><strong>Country:</strong> {geo.country || 'Not set'}</Typography>
              <Typography><strong>Region:</strong> {geo.region || 'Not set'}</Typography>
              <Typography><strong>City:</strong> {geo.city || 'Not set'}</Typography>
              <Typography><strong>IP Address:</strong> {geo.ip || 'Not set'}</Typography>
              <Typography><strong>Coordinates:</strong> 
                {geo.latitude && geo.longitude ? 
                  `${geo.latitude}, ${geo.longitude}` : 'Not set'}
              </Typography>
              
              {geo.latitude && geo.longitude && (
                <UserLocationMap latitude={geo.latitude} longitude={geo.longitude} />
              )}
            </Box>
          </Grid>

          {/* Activity Information */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Activity</Typography>
            <Box sx={{ pl: 2 }}>
              <Typography><strong>Last Online:</strong> {formatDate(user.lastOnline)}</Typography>
              <Typography><strong>Last Suggestions Time:</strong> {formatDate(user.lastSuggestionsTime)}</Typography>
              <Typography><strong>Last Array Time:</strong> {formatDate(user.lastArrayTime)}</Typography>
              <Typography><strong>Is Online:</strong> {user.isOnline ? 'Yes' : 'No'}</Typography>
              <Typography><strong>Play Status:</strong> {user.play ? 'Active' : 'Inactive'}</Typography>
              <Typography><strong>OS:</strong> {user.os || 'Not set'}</Typography>
            </Box>
          </Grid>

          {/* Search Preferences */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Search Preferences</Typography>
            <Box sx={{ pl: 2 }}>
              <Typography><strong>Show Me:</strong> {search.showMe || 'Not set'}</Typography>
              <Typography><strong>Age Range:</strong> {search.ageMin && search.ageMax ? 
                `${search.ageMin} - ${search.ageMax}` : 'Not set'}</Typography>
              <Typography><strong>Show In Search:</strong> {search.showInSearch ? 'Yes' : 'No'}</Typography>
              <Typography><strong>Show In Search For:</strong> {search.showInSearchFor || 'Not set'}</Typography>
            </Box>
          </Grid>

          {/* Rates Information */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Ratings</Typography>
            <Box sx={{ pl: 2 }}>
              <Typography><strong>Overall Score:</strong> {rates.score || 'No ratings'}</Typography>
              <Typography><strong>Number of Rates:</strong> {rates.numberOfRates || 0}</Typography>
              <Typography><strong>Balance:</strong> {rates.balance || 0}</Typography>
              
              {rates.profile && (
                <>
                  <Typography>
                    <strong>Profile:</strong> Score: {rates.profile.score || 0}, 
                    Rates: {rates.profile.numberOfRates || 0},
                    Sum: {rates.profile.summOfRates || 0}
                  </Typography>
                  {rates.profile.whoVoted && rates.profile.whoVoted.length > 0 && (
                    <Accordion sx={{ mt: 1, mb: 2, bgcolor: 'background.paper' }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">
                          Profile Votes ({rates.profile.whoVoted.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List dense disablePadding>
                          {rates.profile.whoVoted.map((vote, index) => (
                            <ListItem key={index} divider={index < rates.profile.whoVoted.length - 1}>
                              <ListItemText 
                                primary={
                                  <Box component="span">
                                    User: {
                                      vote.uid ? (
                                        <Button 
                                          variant="text" 
                                          size="small" 
                                          href={`/dashboard/user/${vote.uid}`}
                                          sx={{ 
                                            p: 0,
                                            minWidth: 'auto',
                                            textTransform: 'none',
                                            fontSize: 'inherit',
                                            fontWeight: 'normal',
                                            justifyContent: 'flex-start'
                                          }}
                                        >
                                          {vote.uid}
                                        </Button>
                                      ) : 'Unknown'
                                    }
                                  </Box>
                                }
                                secondary={
                                  <Typography component="span">
                                    Mark: <Chip 
                                      size="small" 
                                      color={vote.mark >= 4 ? "success" : vote.mark >= 3 ? "primary" : "error"} 
                                      label={vote.mark} 
                                      sx={{ fontSize: '0.75rem', height: '20px' }} 
                                    />
                                  </Typography>
                                } 
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </>
              )}
              
              {rates.photos && (
                <>
                  <Typography>
                    <strong>Photos:</strong> Score: {rates.photos.score || 0}, 
                    Rates: {rates.photos.numberOfRates || 0},
                    Sum: {rates.photos.summOfRates || 0}
                  </Typography>
                  {rates.photos.whoVoted && rates.photos.whoVoted.length > 0 && (
                    <Accordion sx={{ mt: 1, mb: 2, bgcolor: 'background.paper' }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">
                          Photos Votes ({rates.photos.whoVoted.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List dense disablePadding>
                          {rates.photos.whoVoted.map((vote, index) => (
                            <ListItem key={index} divider={index < rates.photos.whoVoted.length - 1}>
                              <ListItemText 
                                primary={
                                  <Box component="span">
                                    User: {
                                      vote.uid ? (
                                        <Button 
                                          variant="text" 
                                          size="small" 
                                          href={`/dashboard/user/${vote.uid}`}
                                          sx={{ 
                                            p: 0,
                                            minWidth: 'auto',
                                            textTransform: 'none',
                                            fontSize: 'inherit',
                                            fontWeight: 'normal',
                                            justifyContent: 'flex-start'
                                          }}
                                        >
                                          {vote.uid}
                                        </Button>
                                      ) : 'Unknown'
                                    }
                                  </Box>
                                }
                                secondary={
                                  <Typography component="span">
                                    Mark: <Chip 
                                      size="small" 
                                      color={vote.mark >= 4 ? "success" : vote.mark >= 3 ? "primary" : "error"} 
                                      label={vote.mark} 
                                      sx={{ fontSize: '0.75rem', height: '20px' }} 
                                    />
                                  </Typography>
                                } 
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </>
              )}
              
              {rates.friends && (
                <>
                  <Typography>
                    <strong>Friends:</strong> Score: {rates.friends.score || 0}, 
                    Rates: {rates.friends.numberOfRates || 0},
                    Sum: {rates.friends.summOfRates || 0}
                  </Typography>
                  {rates.friends.whoVoted && rates.friends.whoVoted.length > 0 && (
                    <Accordion sx={{ mt: 1, mb: 2, bgcolor: 'background.paper' }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">
                          Friends Votes ({rates.friends.whoVoted.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List dense disablePadding>
                          {rates.friends.whoVoted.map((vote, index) => (
                            <ListItem key={index} divider={index < rates.friends.whoVoted.length - 1}>
                              <ListItemText 
                                primary={
                                  <Box component="span">
                                    User: {
                                      vote.uid ? (
                                        <Button 
                                          variant="text" 
                                          size="small" 
                                          href={`/dashboard/user/${vote.uid}`}
                                          sx={{ 
                                            p: 0,
                                            minWidth: 'auto',
                                            textTransform: 'none',
                                            fontSize: 'inherit',
                                            fontWeight: 'normal',
                                            justifyContent: 'flex-start'
                                          }}
                                        >
                                          {vote.uid}
                                        </Button>
                                      ) : 'Unknown'
                                    }
                                  </Box>
                                }
                                secondary={`Mark: ${vote.mark}, Date: ${formatDate(vote.timestamp)}`} 
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </>
              )}
              
              <Typography><strong>Vote Bar:</strong> {user.votebar ? 'Yes' : 'No'}</Typography>
            </Box>
          </Grid>

          {/* Settings */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Settings</Typography>
            <Box sx={{ pl: 2 }}>
              <Typography><strong>Language:</strong> {user.language || 'Not set'}</Typography>
              <Typography><strong>Rating Visible:</strong> {settings.ratingIsVisible ? 'Yes' : 'No'}</Typography>
              <Typography><strong>Show Me Rates:</strong> {settings.showMeRates ? 'Yes' : 'No'}</Typography>
              <Typography><strong>FCM Token:</strong> {settings.fcmToken ? 'Set' : 'Not set'}</Typography>
              <Typography><strong>QR Code:</strong> {settings.qr ? settings.qr : 'Not set'}</Typography>
            </Box>
          </Grid>

          {/* Notifications */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Notification Settings</Typography>
            <Box sx={{ pl: 2 }}>
              {notifications.push && (
                <>
                  <Typography variant="subtitle2">Push Notifications:</Typography>
                  <Typography><strong>Matches:</strong> {notifications.push.matches ? 'On' : 'Off'}</Typography>
                  <Typography><strong>Messages:</strong> {notifications.push.messages ? 'On' : 'Off'}</Typography>
                  <Typography><strong>Suggestions:</strong> {notifications.push.suggestions ? 'On' : 'Off'}</Typography>
                </>
              )}
              
              {notifications.mail && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>Email Notifications:</Typography>
                  <Typography><strong>Matches:</strong> {notifications.mail.matches ? 'On' : 'Off'}</Typography>
                  <Typography><strong>Messages:</strong> {notifications.mail.messages ? 'On' : 'Off'}</Typography>
                  <Typography><strong>Suggestions:</strong> {notifications.mail.suggestions ? 'On' : 'Off'}</Typography>
                  <Typography><strong>Promo:</strong> {notifications.mail.promo ? 'On' : 'Off'}</Typography>
                </>
              )}
            </Box>
          </Grid>

          {/* Subscription */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Subscription</Typography>
            <Box sx={{ pl: 2 }}>
              <Typography><strong>Type:</strong> {subscribtion.type || 'No subscription'}</Typography>
              {subscribtion.created && (
                <Typography><strong>Created:</strong> {formatDate(subscribtion.created)}</Typography>
              )}
              {subscribtion.data && (
                <>
                  <Typography component="div" sx={{ mt: 1 }}><strong>Data:</strong></Typography>
                  <Box 
                    sx={{ 
                      mt: 0.5,
                      p: 1, 
                      bgcolor: 'background.paper', 
                      border: '1px solid', 
                      borderColor: 'divider',
                      borderRadius: 1,
                      maxWidth: '100%',
                      overflowX: 'auto',
                      overflowY: 'auto',
                      maxHeight: '300px',
                      wordBreak: 'break-word',
                      '& pre': {
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        fontSize: '0.875rem',
                        fontFamily: 'monospace'
                      }
                    }}
                  >
                    <Typography component="pre" variant="body2">
                      {subscribtion.data}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Grid>

          {/* Subscription management */}
          <Grid item xs={12}>
            <SubscriptionEditor user={user} onSubscriptionUpdated={handleSubscriptionUpdated} />
          </Grid>
          
          {/* User deletion dialog */}
          <UserDeleteDialog 
            open={deleteDialogOpen}
            onClose={handleCloseDeleteDialog}
            user={user}
            onUserDeleted={handleUserDeleted}
          />

          {/* References */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>References</Typography>
            <Box sx={{ pl: 2 }}>
              <Typography><strong>Link:</strong> {user.link || 'Not set'}</Typography>
              <Typography><strong>Ref:</strong> {user.ref || 'Not set'}</Typography>
            </Box>
          </Grid>

          {/* Arrays */}
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Last Array ({lastArray.length} users)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {renderArray(lastArray)}
              </AccordionDetails>
            </Accordion>
            
            {user.suggestions && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Suggestions ({user.suggestions.length} users)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {renderArray(user.suggestions)}
                </AccordionDetails>
              </Accordion>
            )}
            
            {user.suggestionsFull !== undefined && (
              <Typography sx={{ mt: 1 }}>
                <strong>Suggestions Full:</strong> {user.suggestionsFull ? 'Yes' : 'No'}
              </Typography>
            )}
            
            {user.rateprofiledoc && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Rate Profile Doc ({user.rateprofiledoc.length} users)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {renderArray(user.rateprofiledoc)}
                </AccordionDetails>
              </Accordion>
            )}
            
            {user.ratephotodoc && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Rate Photo Doc ({user.ratephotodoc.length} users)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {renderArray(user.ratephotodoc)}
                </AccordionDetails>
              </Accordion>
            )}
            
            {user.userblockdoc && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">User Block Doc ({user.userblockdoc.length} users)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {renderArray(user.userblockdoc)}
                </AccordionDetails>
              </Accordion>
            )}
            
            {user.whoVotedNotExist && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Who Voted Not Exist ({user.whoVotedNotExist.length} users)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {renderArray(user.whoVotedNotExist)}
                </AccordionDetails>
              </Accordion>
            )}
            
            <Box sx={{ mt: 2, mb: 2 }}>
              <Button 
                variant="outlined" 
                color="primary"
                size="medium"
                href={`/dashboard/user/${user.id}/referrals`}
                startIcon={<PersonAddIcon />}
              >
                View Referrals
              </Button>
            </Box>
          </Grid>

          {/* Votes */}
          {rates.whoVoted && (
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Vote History ({rates.whoVoted.length} votes)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {rates.whoVoted.map((vote, index) => (
                      <ListItem key={index} divider={index < rates.whoVoted.length - 1}>
                        <ListItemText 
                          primary={
                            <Box component="span">
                              User: {
                                vote.uid ? (
                                  <Button 
                                    variant="text" 
                                    size="small" 
                                    href={`/dashboard/user/${vote.uid}`}
                                    sx={{ 
                                      p: 0,
                                      minWidth: 'auto',
                                      textTransform: 'none',
                                      fontSize: 'inherit',
                                      fontWeight: 'normal',
                                      justifyContent: 'flex-start'
                                    }}
                                  >
                                    {vote.uid}
                                  </Button>
                                ) : 'Unknown'
                              }
                            </Box>
                          }
                          secondary={`Mark: ${vote.mark}`} 
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}

          {/* Photos */}
          {photos.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Photos ({photos.length})</Typography>
              <Grid container spacing={1}>
                {photos.map((photo, index) => (
                  <Grid item key={photo.uid || index} xs={6} sm={4} md={3} lg={2}>
                    <Box 
                      component="a"
                      href={photo.image}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        position: 'relative',
                        height: 0,
                        paddingTop: '100%',
                        backgroundColor: 'grey.200',
                        backgroundImage: photo.image ? `url(${photo.image})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        borderRadius: 1,
                        display: 'block',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: 3,
                          '&::after': {
                            content: '"View Full Size"',
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: 'white',
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            whiteSpace: 'nowrap'
                          }
                        }
                      }}
                    >
                      <Box 
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          bgcolor: 'rgba(0,0,0,0.6)',
                          color: 'white',
                          px: 1,
                          borderTopLeftRadius: 4
                        }}
                      >
                        {photo.score || '0'}
                      </Box>
                      <Box 
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          bgcolor: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          px: 1,
                          borderBottomLeftRadius: 4,
                          fontSize: '0.75rem'
                        }}
                      >
                        <Typography component="span" sx={{ fontWeight: 'bold' }}>
                          {photo.numberOfRates !== undefined ? `${photo.numberOfRates} votes` : 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
        </Grid>
      )}
    </Paper>
  );
};

export default UserDetails;
